import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/leads.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import PrintReportButton from '../components/PrintReportButton';
import DateRangeFilter from '../components/DateRangeFilter';
import StudentSearchSelect from '../components/StudentSearchSelect';
import ListPagination from '../components/ListPagination';
import useListPagination from '../hooks/useListPagination';
import { EMPTY_DATE_RANGE, hasDateRange, isDateInRange, startOfMonthYMD, todayYMD } from '../utils/dateRange';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet, faPlus, faEdit, faTrash, faSearch, faFilePdf,
  faUniversity, faUser, faCheckCircle,
  faBuilding, faTimes, faMoneyBillWave, faUserTie, faHandshake,
  faArrowUp, faArrowDown, faExclamationCircle,
  faExchangeAlt, faMoneyBill, faToggleOn, faToggleOff,
  faEye, faEyeSlash, faReceipt, faChevronDown, faCreditCard, faCheck,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';

const STATUS_FILTER_OPTIONS = ['Paid', 'Pending', 'Partially Paid'];
const INSTALLMENT_FILTER_OPTIONS = [
  { value: 'with', label: 'With Installment Plan' },
  { value: 'without', label: 'Without Installment Plan' },
];

const currentMonthRange = () => ({ from: startOfMonthYMD(), to: todayYMD() });

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const STATUS_COLORS = {
  'Paid':           { bg: '#E8F5E9', color: '#2E7D32' },
  'Pending':        { bg: '#FFF8E1', color: '#F57F17' },
  'Partially Paid': { bg: '#E3F2FD', color: '#1565C0' },
  'Cancelled':      { bg: '#FFEBEE', color: '#C62828' },
};

const TYPE_CONFIG = {
  'Student':              { icon: faUser,       bg: '#E3F2FD', color: '#1565C0' },
  'University Commission':{ icon: faUniversity, bg: '#F3E5F5', color: '#6A1B9A' },
  'Agent Commission':     { icon: faHandshake,  bg: '#FFF3E0', color: '#E65100' },
};

const CURRENCY_FLAGS   = { PKR: '🇵🇰', USD: '🇺🇸', GBP: '🇬🇧', EUR: '🇪🇺' };
const CURRENCY_SYMBOLS = { PKR: '₨', USD: '$', GBP: '£', EUR: '€' };

// ─────────────────────────────────────────────
// Sub-modal: Manage Agents
// ─────────────────────────────────────────────
function AgentsModal({ onClose }) {
  const [agents, setAgents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [form, setForm] = useState({ agentName: '', companyName: '', email: '', phone: '', address: '', status: 'Active', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/finance/agents`);
      const data = await res.json();
      if (data.success) setAgents(data.agents);
    } catch { setError('Failed to load agents'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAgents(); }, []);

  const resetForm = () => { setForm({ agentName: '', companyName: '', email: '', phone: '', address: '', status: 'Active', notes: '' }); setEditAgent(null); setShowForm(false); };

  const openEdit = (agent) => {
    setEditAgent(agent);
    setForm({ agentName: agent.agent_name, companyName: agent.company_name || '', email: agent.email || '', phone: agent.phone || '', address: agent.address || '', status: agent.status, notes: agent.notes || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.agentName.trim()) { setError('Agent name is required'); return; }
    setSaving(true); setError('');
    try {
      const url    = editAgent ? `${API_URL}/finance/agents/${editAgent.id}` : `${API_URL}/finance/agents`;
      const method = editAgent ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data   = await res.json();
      if (data.success) { resetForm(); fetchAgents(); }
      else setError(data.message);
    } catch { setError('Failed to save agent'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this agent? This cannot be undone.')) return;
    try {
      const res  = await fetch(`${API_URL}/finance/agents/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchAgents();
      else setError(data.message);
    } catch { setError('Failed to delete agent'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '620px' }}>
        <div className="modal-header">
          <h2><FontAwesomeIcon icon={faUserTie} style={{ marginRight: '10px', color: '#F47B20' }} />Manage Agents</h2>
          <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
          {showForm ? (
            <div style={{ background: '#f9f9f9', borderRadius: '10px', padding: '20px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '16px', color: '#1a1a1a' }}>{editAgent ? 'Edit Agent' : 'Add New Agent'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Agent Name <span style={{ color: 'red' }}>*</span></label>
                  <input className="input-field" value={form.agentName} onChange={e => setForm({ ...form, agentName: e.target.value })} placeholder="Full name" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Company Name</label>
                  <input className="input-field" value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} placeholder="Optional" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Email</label>
                  <input className="input-field" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="agent@email.com" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Phone</label>
                  <input className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+92..." />
                </div>
                <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <label>Address</label>
                  <input className="input-field" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Optional" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Status</label>
                  <select className="input-field" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Notes</label>
                  <input className="input-field" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button onClick={handleSave} disabled={saving} style={{ background: '#F47B20', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                  {saving ? 'Saving...' : (editAgent ? 'Update Agent' : 'Add Agent')}
                </button>
                <button onClick={resetForm} style={{ background: '#e5e7eb', color: '#555', border: 'none', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowForm(true)} style={{ background: '#F47B20', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FontAwesomeIcon icon={faPlus} /> Add Agent
            </button>
          )}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>Loading...</div>
          ) : agents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#aaa', fontSize: '13px' }}>No agents yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {agents.map(agent => (
                <div key={agent.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', borderRadius: '10px', padding: '14px 16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={faUserTie} style={{ color: '#F47B20', fontSize: '14px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#1a1a1a' }}>{agent.agent_name}</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>{[agent.company_name, agent.email, agent.phone].filter(Boolean).join(' · ')}</div>
                  </div>
                  <span style={{ background: agent.status === 'Active' ? '#E8F5E9' : '#f5f5f5', color: agent.status === 'Active' ? '#2E7D32' : '#888', padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>{agent.status}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>{agent.total_invoices} inv.</span>
                  <button onClick={() => openEdit(agent)} style={{ background: '#e3f2fd', color: '#1565c0', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' }}><FontAwesomeIcon icon={faEdit} /></button>
                  <button onClick={() => handleDelete(agent.id)} style={{ background: '#ffebee', color: '#c62828', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' }}><FontAwesomeIcon icon={faTrash} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Finance Page
// ─────────────────────────────────────────────
export default function Finance() {
  const navigate = useNavigate();
  const [invoices, setInvoices]         = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [students, setStudents]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const [searchTerm, setSearchTerm]     = useState('');
  const [dateRange, setDateRange]       = useState(currentMonthRange);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [installmentFilter, setInstallmentFilter] = useState(''); // '' | 'with' | 'without'
  const [activeFilter, setActiveFilter] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [columnFilters, setColumnFilters] = useState({
    type: [], status: []
  });
  const [filterSearch, setFilterSearch] = useState({
    type: '', status: ''
  });

  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [invoiceToDelete, setInvoiceToDelete]   = useState(null);
  const [deleting, setDeleting]                 = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice]     = useState(null);
  const [paymentHistory, setPaymentHistory]     = useState([]);
  const [paymentInstallments, setPaymentInstallments] = useState([]);
  const [paymentForm, setPaymentForm]           = useState({
    paymentStatus: '', installmentAmount: '', paymentDate: '',
    paymentMethod: 'bank', bankAccountId: ''
  });
  const [updatingPayment, setUpdatingPayment]   = useState(false);
  const [receiptPicker, setReceiptPicker]       = useState(null); // { invoice, payments }

  const [showPlanModal, setShowPlanModal]       = useState(false);
  const [planInvoice, setPlanInvoice]           = useState(null);
  const [planInstallments, setPlanInstallments] = useState([]);
  const [planPayments, setPlanPayments]         = useState([]);
  const [planLoading, setPlanLoading]           = useState(false);

  const [showAgentsModal, setShowAgentsModal] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/finance/invoices`);
      const data = await res.json();
      if (data.success) setInvoices(data.invoices);
    } catch { setError('Failed to fetch invoices'); }
    finally { setLoading(false); }
  }, []);

  const fetchBankAccounts = async () => {
    try {
      const res  = await fetch(`${API_URL}/finance/bank-accounts`);
      const data = await res.json();
      if (data.success) setBankAccounts(data.accounts.filter(a => a.status === 'Active'));
    } catch { console.error('Bank accounts error'); }
  };

  const fetchStudents = async () => {
    try {
      const res  = await fetch(`${API_URL}/students`);
      const data = await res.json();
      if (data.success) setStudents(data.students || []);
    } catch { console.error('Students error'); }
  };

  useEffect(() => {
    fetchInvoices();
    fetchBankAccounts();
    fetchStudents();
  }, [fetchInvoices]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown-container')) {
        setActiveFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUniqueValues = (column) => {
    const values = new Set();
    invoices.forEach(inv => {
      let value;
      if (column === 'type') value = inv.invoice_type;
      else if (column === 'status') value = inv.payment_status;
      if (value) values.add(value);
    });
    return Array.from(values).sort();
  };

  const getFilteredOptions = (column) => {
    const unique = getUniqueValues(column);
    const term = (filterSearch[column] || '').toLowerCase();
    return term ? unique.filter(v => v.toLowerCase().includes(term)) : unique;
  };

  const toggleFilterValue = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: prev[column].includes(value)
        ? prev[column].filter(v => v !== value)
        : [...prev[column], value]
    }));
  };

  const clearColumnFilter = (column) => {
    setColumnFilters(prev => ({ ...prev, [column]: [] }));
    setFilterSearch(prev => ({ ...prev, [column]: '' }));
  };

  const clearAllFilters = () => {
    setColumnFilters({ type: [], status: [] });
    setFilterSearch({ type: '', status: '' });
    setSearchTerm('');
    setSelectedStudentId('');
    setStatusFilter('');
    setInstallmentFilter('');
    setDateRange(currentMonthRange());
  };

  const selectedStudent = useMemo(
    () => students.find(s => String(s.id) === String(selectedStudentId)) || null,
    [students, selectedStudentId]
  );

  const filteredInvoices = invoices.filter(inv => {
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      const matchesSearch = (
        inv.invoice_id?.toLowerCase().includes(s) ||
        inv.student_name?.toLowerCase().includes(s) ||
        inv.manual_student_name?.toLowerCase().includes(s) ||
        inv.student_ref_id?.toLowerCase().includes(s) ||
        inv.university_name?.toLowerCase().includes(s) ||
        inv.account_name?.toLowerCase().includes(s) ||
        inv.agent_name?.toLowerCase().includes(s)
      );
      if (!matchesSearch) return false;
    }
    if (columnFilters.type.length > 0 && !columnFilters.type.includes(inv.invoice_type)) return false;
    if (columnFilters.status.length > 0 && !columnFilters.status.includes(inv.payment_status)) return false;
    if (statusFilter && inv.payment_status !== statusFilter) return false;
    if (installmentFilter === 'with') {
      if (!(inv.has_installment_plan === 1 || inv.has_installment_plan === true)) return false;
    } else if (installmentFilter === 'without') {
      if (inv.has_installment_plan === 1 || inv.has_installment_plan === true) return false;
    }
    if (hasDateRange(dateRange) && !isDateInRange(inv.invoice_date, dateRange.from, dateRange.to)) return false;
    if (selectedStudentId) {
      const matchesId = String(inv.student_id || '') === String(selectedStudentId);
      const selectedName = selectedStudent
        ? [selectedStudent.name, selectedStudent.middle_name, selectedStudent.surname].filter(Boolean).join(' ').trim().toLowerCase()
        : '';
      const invName = (inv.student_name || inv.manual_student_name || '').trim().toLowerCase();
      const matchesName = selectedName && invName && invName === selectedName;
      if (!matchesId && !matchesName) return false;
    }
    return true;
  });

  const statistics = useMemo(() => {
    let total_credit = 0;
    let total_debit = 0;
    let total_pending = 0;
    let total_agent_payout = 0;
    let paid_count = 0;
    let pending_count = 0;

    filteredInvoices.forEach((inv) => {
      const finalAmt = parseFloat(inv.final_amount) || 0;
      const paidAmt = parseFloat(inv.paid_amount) || 0;
      if (inv.invoice_type === 'Agent Commission') {
        total_agent_payout += finalAmt;
        return;
      }
      if (inv.payment_status === 'Paid') {
        total_credit += finalAmt;
        paid_count += 1;
      }
      if (inv.payment_status === 'Pending' || inv.payment_status === 'Partially Paid') {
        total_debit += finalAmt;
        total_pending += Math.max(0, finalAmt - paidAmt);
      }
      if (inv.payment_status === 'Pending') pending_count += 1;
    });

    return {
      total_credit,
      total_debit,
      total_pending,
      total_agent_payout,
      paid_count,
      pending_count,
      total_invoices: filteredInvoices.length,
    };
  }, [filteredInvoices]);

  const { paginatedItems: paginatedInvoices, totalPages, currentPage, goToPage } = useListPagination(
    filteredInvoices,
    [searchTerm, JSON.stringify(columnFilters), dateRange.from, dateRange.to, selectedStudentId, statusFilter, installmentFilter].join('|')
  );

  const hasActiveFilters = Object.values(columnFilters).some(f => f.length > 0)
    || searchTerm.trim()
    || hasDateRange(dateRange)
    || Boolean(selectedStudentId)
    || Boolean(statusFilter)
    || Boolean(installmentFilter);

  const FilterDropdown = ({ column, label }) => {
    const triggerRef = useRef(null);
    const isActive = activeFilter === column;
    const hasFilters = columnFilters[column].length > 0;
    const filteredOptions = getFilteredOptions(column);
    const allOptions = getUniqueValues(column);

    const handleToggle = () => {
      if (isActive) { setActiveFilter(null); return; }
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 380;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove)
        ? rect.bottom + 8
        : rect.top - dropdownHeight - 8;
      let left = rect.left;
      if (left + 280 > window.innerWidth) left = window.innerWidth - 300;
      setDropdownPosition({ top, left });
      setActiveFilter(column);
    };

    return (
      <div className="filter-dropdown-container">
        <button
          ref={triggerRef}
          className={`filter-trigger ${hasFilters ? 'has-filters' : ''}`}
          onClick={handleToggle}
        >
          {label}
          <FontAwesomeIcon icon={faChevronDown} className="filter-icon" />
          {hasFilters && <span className="filter-count">{columnFilters[column].length}</span>}
        </button>
        {isActive && (
          <div className="filter-dropdown" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}>
            <div className="filter-dropdown-header">
              <input
                type="text"
                className="filter-search-input"
                placeholder="Search..."
                value={filterSearch[column]}
                onChange={(e) => setFilterSearch(prev => ({ ...prev, [column]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="filter-options">
              <label className="filter-option select-all">
                <input
                  type="checkbox"
                  checked={columnFilters[column].length === allOptions.length && allOptions.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) setColumnFilters(prev => ({ ...prev, [column]: allOptions }));
                    else clearColumnFilter(column);
                  }}
                />
                <span className="filter-label">Select All</span>
              </label>
              {filteredOptions.length > 0 ? filteredOptions.map((value, i) => (
                <label key={i} className="filter-option">
                  <input
                    type="checkbox"
                    checked={columnFilters[column].includes(value)}
                    onChange={() => toggleFilterValue(column, value)}
                  />
                  <span className="filter-label">{value}</span>
                </label>
              )) : (
                <div className="no-results">No results found</div>
              )}
            </div>
            <div className="filter-dropdown-footer">
              <button className="btn-clear-filter" onClick={() => clearColumnFilter(column)}>Clear</button>
              <button className="btn-apply-filter" onClick={() => setActiveFilter(null)}>Apply</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    setDeleting(true);
    try {
      const res  = await fetch(`${API_URL}/finance/invoices/${invoiceToDelete.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { setShowDeleteModal(false); setInvoiceToDelete(null); fetchInvoices(); }
      else setError(data.message);
    } catch { setError('Failed to delete invoice'); }
    finally { setDeleting(false); }
  };

  const loadPaymentHistory = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/finance/invoices/${invoiceId}/payments`);
      const data = await res.json();
      if (data.success) {
        setPaymentHistory(data.payments || []);
        return data.payments || [];
      }
    } catch { /* ignore */ }
    setPaymentHistory([]);
    return [];
  };

  const openPlanHistoryModal = async (inv) => {
    setPlanInvoice(inv);
    setPlanInstallments([]);
    setPlanPayments(inv.payments || []);
    setShowPlanModal(true);
    setPlanLoading(true);
    try {
      const res = await fetch(`${API_URL}/finance/invoices/${inv.id}`);
      const data = await res.json();
      if (data.success) {
        if (data.invoice) setPlanInvoice(prev => ({ ...prev, ...data.invoice }));
        setPlanInstallments(data.installments || []);
        setPlanPayments(data.payments || inv.payments || []);
      } else {
        const payments = await loadPaymentHistory(inv.id);
        setPlanPayments(payments || []);
      }
    } catch {
      const payments = await loadPaymentHistory(inv.id);
      setPlanPayments(payments || []);
    } finally {
      setPlanLoading(false);
    }
  };

  const openPaymentModal = async (inv) => {
    setPaymentInvoice(inv);
    setPaymentForm({
      paymentStatus: inv.payment_status,
      installmentAmount: '',
      paymentDate: new Date().toISOString().slice(0, 10),
      paymentMethod: inv.payment_method === 'cash' ? 'cash' : 'bank',
      bankAccountId: inv.bank_account_id ? String(inv.bank_account_id) : ''
    });
    setPaymentInstallments([]);
    setShowPaymentModal(true);
    const payments = inv.payments?.length ? inv.payments : await loadPaymentHistory(inv.id);
    setPaymentHistory(payments || []);
    try {
      const res = await fetch(`${API_URL}/finance/invoices/${inv.id}`);
      const data = await res.json();
      if (data.success) {
        setPaymentInstallments(data.installments || []);
        if (data.invoice) setPaymentInvoice(prev => ({ ...prev, ...data.invoice }));
        if (data.payments?.length) setPaymentHistory(data.payments);
      }
    } catch { /* ignore */ }
  };

  const openReceiptForInvoice = async (inv) => {
    let payments = Array.isArray(inv.payments) ? inv.payments : null;
    if (!payments) {
      try {
        const res = await fetch(`${API_URL}/finance/invoices/${inv.id}/payments`);
        const data = await res.json();
        payments = data.success ? (data.payments || []) : [];
      } catch {
        payments = [];
      }
    }

    if (payments.length > 1) {
      setReceiptPicker({ invoice: inv, payments });
      return;
    }
    if (payments.length === 1) {
      navigate(`/payment-receipt/${inv.id}/${payments[0].id}`);
      return;
    }
    // Fallback for paid invoices without history rows yet
    navigate(`/payment-receipt/${inv.id}`);
  };

  const handleUpdatePayment = async () => {
    if (!paymentInvoice) return;
    const amount = parseFloat(paymentForm.installmentAmount);
    const hasInstallment = isFinite(amount) && amount > 0;

    if (hasInstallment && !paymentForm.paymentDate) {
      setError('Payment date is required for a new payment');
      return;
    }
    if (hasInstallment && paymentForm.paymentMethod === 'bank' && !paymentForm.bankAccountId) {
      setError('Please select a bank account for this payment');
      return;
    }

    setUpdatingPayment(true);
    setError('');
    try {
      if (hasInstallment) {
        const res = await fetch(`${API_URL}/finance/invoices/${paymentInvoice.id}/payments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            paymentDate: paymentForm.paymentDate,
            paymentStatus: paymentForm.paymentStatus,
            paymentMethod: paymentForm.paymentMethod,
            bankAccountId: paymentForm.paymentMethod === 'bank' && paymentForm.bankAccountId
              ? parseInt(paymentForm.bankAccountId, 10)
              : null
          })
        });
        const data = await res.json();
        if (!data.success) { setError(data.message); return; }
        setPaymentHistory(data.payments || []);
        setPaymentForm(prev => ({
          ...prev,
          installmentAmount: '',
          paymentStatus: data.paymentStatus || prev.paymentStatus,
          paymentDate: new Date().toISOString().slice(0, 10)
        }));
        setPaymentInvoice(prev => prev ? {
          ...prev,
          paid_amount: data.paidAmount,
          payment_status: data.paymentStatus,
          payment_method: paymentForm.paymentMethod,
          bank_account_id: paymentForm.paymentMethod === 'bank' ? paymentForm.bankAccountId : null,
          payments: data.payments
        } : prev);
        try {
          const detailRes = await fetch(`${API_URL}/finance/invoices/${paymentInvoice.id}`);
          const detailData = await detailRes.json();
          if (detailData.success) setPaymentInstallments(detailData.installments || []);
        } catch { /* ignore */ }
        fetchInvoices();
      } else {
        // Status-only update (e.g. Cancelled)
        const res = await fetch(`${API_URL}/finance/invoices/${paymentInvoice.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentStatus: paymentForm.paymentStatus })
        });
        const data = await res.json();
        if (!data.success) { setError(data.message); return; }
        setShowPaymentModal(false);
        setPaymentInvoice(null);
        fetchInvoices();
      }
    } catch { setError('Failed to update payment'); }
    finally { setUpdatingPayment(false); }
  };

  const canViewReceipt = (inv) =>
    ['Paid', 'Partially Paid'].includes(inv.payment_status) && parseFloat(inv.paid_amount) > 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getBilledToDisplay = (inv) => {
    if (inv.invoice_type === 'Student') {
      const name = inv.manual_student_name || inv.student_name || '-';
      const ref = inv.manual_student_name ? '' : (inv.student_ref_id || '');
      return ref ? `${name} (${ref})` : name;
    }
    if (inv.invoice_type === 'Agent Commission') {
      return `${inv.agent_name || '-'} (${inv.agent_commission_percent || 0}% commission)`;
    }
    return inv.university_name || '-';
  };

  const getInvoiceTypeDisplay = (inv) => {
    if (inv.invoice_type === 'University Commission') return 'Uni. Commission';
    if (inv.invoice_type === 'Agent Commission') return 'Agent Comm.';
    return 'Student';
  };

  const printColumns = [
    { header: 'Invoice ID', value: (r) => r.invoice_id },
    { header: 'Type', value: (r) => getInvoiceTypeDisplay(r) },
    { header: 'Date', value: (r) => formatDate(r.invoice_date) },
    { header: 'Billed To / Agent', value: (r) => getBilledToDisplay(r) },
    {
      header: 'Amount (PKR)',
      value: (r) => parseFloat(r.final_amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 }),
    },
    { header: 'Status', value: (r) => r.payment_status },
  ];

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="leads-container">

          <div className="leads-header">
            <div className="header-left">
              <h1><FontAwesomeIcon icon={faWallet} className="page-icon" />Manage Invoices</h1>
            </div>
            <div className="header-right" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <PrintReportButton
                show={hasActiveFilters}
                title="Manage Invoices"
                columns={printColumns}
                rows={filteredInvoices}
              />
              {hasActiveFilters && (
                <button className="btn-clear-all-filters" onClick={clearAllFilters}>
                  <FontAwesomeIcon icon={faTimes} /> Clear All Filters
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowAgentsModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <FontAwesomeIcon icon={faUserTie} /> Agents
              </button>
              <button className="btn-secondary" onClick={() => navigate('/finance/bank-accounts')} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <FontAwesomeIcon icon={faBuilding} /> Bank Accounts
              </button>
              <button className="btn-primary" onClick={() => navigate('/create-invoice')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FontAwesomeIcon icon={faPlus} /> New Invoice
              </button>
            </div>
          </div>

          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>{error}</div>}

          <div className="page-toolbar finance-invoice-filters">
            <div className="page-search" style={{ flex: '1.1 1 200px', minWidth: '180px' }}>
              <FontAwesomeIcon icon={faSearch} className="page-search-icon" />
              <input
                type="text"
                className="page-search-input"
                placeholder="Search by invoice ID, student, university, agent..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button type="button" className="page-search-clear" onClick={() => setSearchTerm('')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            <div className="finance-filter-student">
              <StudentSearchSelect
                students={students}
                value={selectedStudentId}
                onChange={setSelectedStudentId}
              />
            </div>

            <select
              className="input-field finance-filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by payment status"
            >
              <option value="">All Statuses</option>
              {STATUS_FILTER_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            <select
              className="input-field finance-filter-status finance-filter-installment"
              value={installmentFilter}
              onChange={(e) => setInstallmentFilter(e.target.value)}
              aria-label="Filter by installment plan"
            >
              <option value="">Installment Plan (All)</option>
              {INSTALLMENT_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <div className="finance-filter-date">
              <DateRangeFilter
                label="Invoice Date"
                value={dateRange}
                onChange={setDateRange}
              />
              <button
                type="button"
                className="finance-show-all-data"
                onClick={() => setDateRange(EMPTY_DATE_RANGE)}
              >
                Show all data
              </button>
            </div>
          </div>

          {/* Stats — driven by current date / student / status filters */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#E8F5E9' }}><FontAwesomeIcon icon={faArrowDown} style={{ color: '#4CAF50' }} /></div>
              <div className="stat-content">
                <h3>Total Credit</h3>
                <p className="stat-number" style={{ fontSize: '16px', color: '#2E7D32' }}>PKR {parseFloat(statistics.total_credit || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                <span style={{ fontSize: '12px', color: '#666' }}>{statistics.paid_count} paid</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#FFF3E0' }}><FontAwesomeIcon icon={faArrowUp} style={{ color: '#FF9800' }} /></div>
              <div className="stat-content">
                <h3>Total Debit</h3>
                <p className="stat-number" style={{ fontSize: '16px', color: '#E65100' }}>PKR {parseFloat(statistics.total_debit || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                <span style={{ fontSize: '12px', color: '#666' }}>{statistics.pending_count} pending</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#FFEBEE' }}><FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#F44336' }} /></div>
              <div className="stat-content">
                <h3>Pending Amount</h3>
                <p className="stat-number" style={{ fontSize: '16px', color: '#C62828' }}>PKR {parseFloat(statistics.total_pending || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                <span style={{ fontSize: '12px', color: '#666' }}>{statistics.total_invoices} total</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#F3E5F5' }}><FontAwesomeIcon icon={faHandshake} style={{ color: '#6A1B9A' }} /></div>
              <div className="stat-content">
                <h3>Agent Payouts</h3>
                <p className="stat-number" style={{ fontSize: '16px', color: '#6A1B9A' }}>PKR {parseFloat(statistics.total_agent_payout || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}</p>
                <span style={{ fontSize: '12px', color: '#666' }}>commissions</span>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading invoices...</div>
          ) : (
            <>
            <div className="leads-table-container">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th><FilterDropdown column="type" label="Type" /></th>
                    <th>Date</th>
                    <th>Billed To / Agent</th>
                    <th>Amount (PKR)</th>
                    <th><FilterDropdown column="status" label="Status" /></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length > 0 ? paginatedInvoices.map(inv => {
                    const typeConf = TYPE_CONFIG[inv.invoice_type] || TYPE_CONFIG['Student'];
                    const hasForeignCurrency = inv.selected_currency && inv.selected_currency !== 'PKR';
                    const isManualStudent = inv.is_manual_student;
                    // show_converted_currency: DB returns 0/1, treat missing as 1 (default show)
                    const showConverted = hasForeignCurrency && (inv.show_converted_currency !== 0 && inv.show_converted_currency !== false);
                    const currencyHidden = hasForeignCurrency && !showConverted;

                    // Display name for billed-to column
                    const studentDisplayName = isManualStudent ? inv.manual_student_name : inv.student_name;

                    return (
                      <tr key={inv.id}>
                        <td>
                          <strong style={{ color: '#F47B20' }}>{inv.invoice_id}</strong>
                          {inv.invoice_type === 'Agent Commission' && (
                            <div style={{ fontSize: '10px', color: '#9C27B0', fontWeight: 600 }}>DEBIT</div>
                          )}
                          {hasForeignCurrency && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px', flexWrap: 'wrap' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: '#E3F2FD', color: '#1565C0', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>
                                <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '8px' }} />
                                {CURRENCY_FLAGS[inv.selected_currency] || ''} {inv.selected_currency}
                              </span>
                              {currencyHidden && (
                                <span title="Foreign currency hidden on PDF" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: '#FFF3E0', color: '#E65100', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>
                                  <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '8px' }} /> hidden
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: typeConf.bg, color: typeConf.color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                            <FontAwesomeIcon icon={typeConf.icon} />
                            {inv.invoice_type === 'University Commission' ? 'Uni. Commission' :
                             inv.invoice_type === 'Agent Commission' ? 'Agent Comm.' : 'Student'}
                          </span>
                        </td>
                        <td>{formatDate(inv.invoice_date)}</td>
                        <td>
                          {inv.invoice_type === 'Student' ? (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                {studentDisplayName || '-'}
                              </div>
                              {!isManualStudent && <div style={{ color: '#888', fontSize: '11px' }}>{inv.student_ref_id}</div>}
                            </div>
                          ) : inv.invoice_type === 'Agent Commission' ? (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '13px' }}>{inv.agent_name || '-'}</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>{inv.agent_commission_percent}% commission</div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '13px' }}>{inv.university_name || '-'}</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>{inv.commission_reference}</div>
                              {inv.commission_students && inv.commission_students.length > 0 && (
                                <div style={{ color: '#6A1B9A', fontSize: '11px', fontWeight: 600 }}>
                                  {inv.commission_students.length} student{inv.commission_students.length !== 1 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 700 }}>
                            PKR {parseFloat(inv.final_amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                          </div>
                          {inv.discount > 0 && (
                            <div style={{ color: '#4CAF50', fontSize: '11px' }}>-PKR {parseFloat(inv.discount).toLocaleString()}</div>
                          )}
                          {inv.gst_percent > 0 && (
                            <div style={{ color: '#FF9800', fontSize: '11px' }}>+{inv.gst_percent}% GST</div>
                          )}
                          {/* Only show foreign amount if show_converted_currency is on */}
                          {showConverted && inv.converted_amount && (
                            <div style={{ color: '#1565C0', fontSize: '11px', fontWeight: 600 }}>
                              ≈ {CURRENCY_SYMBOLS[inv.selected_currency] || ''}{parseFloat(inv.converted_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} {inv.selected_currency}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{ ...(STATUS_COLORS[inv.payment_status] || { bg: '#f5f5f5', color: '#555' }), padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                            {inv.payment_status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button title="Update Payment" style={{ background: '#e3f2fd', color: '#1565c0', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }} onClick={() => openPaymentModal(inv)}>
                              <FontAwesomeIcon icon={faCheckCircle} />
                            </button>
                            <button title="Edit Invoice" style={{ background: '#FFF3E0', color: '#E65100', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }} onClick={() => navigate(`/edit-invoice/${inv.id}`)}>
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button title="View PDF" style={{ background: '#fce4ec', color: '#c62828', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }} onClick={() => navigate(`/invoice-pdf/${inv.id}`)}>
                              <FontAwesomeIcon icon={faFilePdf} />
                            </button>
                            <button
                              title="Installment Plan & Payment History"
                              style={{ background: '#f3e5f5', color: '#6a1b9a', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }}
                              onClick={() => openPlanHistoryModal(inv)}
                            >
                              <FontAwesomeIcon icon={faClipboardList} />
                            </button>
                            {canViewReceipt(inv) && (
                              <button title="View Receipt" style={{ background: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }} onClick={() => openReceiptForInvoice(inv)}>
                                <FontAwesomeIcon icon={faReceipt} />
                              </button>
                            )}
                            <button className="btn-icon btn-delete" title="Delete" onClick={() => { setInvoiceToDelete(inv); setShowDeleteModal(true); }}>
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                        {hasActiveFilters ? 'No invoices match the selected filters' : 'No invoices found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <ListPagination
              total={filteredInvoices.length}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              itemLabel="invoice"
            />
            </>
          )}
        </div>
      </div>

      {showAgentsModal && <AgentsModal onClose={() => setShowAgentsModal(false)} />}

      {/* Installment Plan & Payment History Modal */}
      {showPlanModal && planInvoice && (
        <div className="modal-overlay" onClick={() => setShowPlanModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px', width: '92vw' }}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '10px', color: '#6A1B9A' }} />
                Plan &amp; History
              </h2>
              <button className="modal-close" onClick={() => setShowPlanModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ fontWeight: 600 }}>{planInvoice.invoice_id}</div>
                <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
                  Final Amount: <strong>PKR {(parseFloat(planInvoice.final_amount) || 0).toLocaleString()}</strong>
                  <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                  Paid: <strong style={{ color: '#2E7D32' }}>PKR {(parseFloat(planInvoice.paid_amount) || 0).toLocaleString()}</strong>
                </div>
              </div>

              {planLoading ? (
                <div style={{ textAlign: 'center', padding: '24px', color: '#888' }}>Loading…</div>
              ) : (
                <>
                  {planInstallments.length > 0 ? (
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontWeight: 700, fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                        Installment Schedule
                      </div>
                      <div style={{ border: '1px solid #FFD8B0', borderRadius: '8px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#FFF8F0' }}>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#E65100', fontWeight: 700 }}>#</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#E65100', fontWeight: 700 }}>Due Date</th>
                              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#E65100', fontWeight: 700 }}>Amount</th>
                              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#E65100', fontWeight: 700 }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {planInstallments.map((row, idx) => (
                              <tr key={row.id || idx} style={{ borderTop: '1px solid #ffe8d1', background: idx % 2 ? '#FFFBF5' : '#fff' }}>
                                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{row.installment_number || idx + 1}</td>
                                <td style={{ padding: '8px 10px' }}>{formatDate(row.due_date)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                                  PKR {parseFloat(row.amount || 0).toLocaleString()}
                                </td>
                                <td style={{
                                  padding: '8px 10px', textAlign: 'right', fontWeight: 600,
                                  color: row.status === 'Paid' ? '#2E7D32' : row.status === 'Partial' ? '#E65100' : '#666'
                                }}>
                                  {row.status || 'Pending'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      marginBottom: '18px', padding: '12px 14px', background: '#fafafa',
                      border: '1px dashed #e5e7eb', borderRadius: '8px', color: '#888', fontSize: '13px'
                    }}>
                      No installment plan on this invoice.
                    </div>
                  )}

                  <div>
                    <div style={{ fontWeight: 700, fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                      Payment History ({planPayments.length})
                    </div>
                    {planPayments.length > 0 ? (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#fafafa' }}>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#666', fontWeight: 600 }}>#</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#666', fontWeight: 600 }}>Date</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#666', fontWeight: 600 }}>Type</th>
                              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#666', fontWeight: 600 }}>Via</th>
                              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#666', fontWeight: 600 }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {planPayments.map((p, idx) => {
                              const via = p.payment_method === 'cash'
                                ? 'Cash'
                                : (p.account_name || p.bank_name || (p.payment_method === 'bank' ? 'Bank' : '—'));
                              return (
                                <tr key={p.id || idx} style={{ borderTop: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '8px 10px' }}>{idx + 1}</td>
                                  <td style={{ padding: '8px 10px' }}>{formatDate(p.payment_date)}</td>
                                  <td style={{ padding: '8px 10px', color: '#555' }}>
                                    {String(p.notes || '').toLowerCase() === 'advance' ? 'Advance' : 'Payment'}
                                  </td>
                                  <td style={{ padding: '8px 10px', color: '#555' }}>{via}</td>
                                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#2E7D32' }}>
                                    PKR {parseFloat(p.amount || 0).toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{
                        padding: '12px 14px', background: '#fafafa',
                        border: '1px dashed #e5e7eb', borderRadius: '8px', color: '#888', fontSize: '13px'
                      }}>
                        No payments recorded yet.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPlanModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Update Payment Modal */}
      {showPaymentModal && paymentInvoice && (() => {
        const finalAmt = parseFloat(paymentInvoice.final_amount) || 0;
        const paidAmt = parseFloat(paymentInvoice.paid_amount) || 0;
        const remaining = Math.max(0, finalAmt - paidAmt);
        return (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '760px', width: '92vw' }}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '10px', color: '#4CAF50' }} />Update Payment</h2>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f9f9f9', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ fontWeight: 600 }}>{paymentInvoice.invoice_id}</div>
                <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
                  Final Amount: <strong>PKR {finalAmt.toLocaleString()}</strong>
                </div>
                <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '12px' }}>
                  <span style={{ color: '#2E7D32' }}>Paid: <strong>PKR {paidAmt.toLocaleString()}</strong></span>
                  <span style={{ color: remaining > 0 ? '#E65100' : '#2E7D32' }}>
                    Remaining: <strong>PKR {remaining.toLocaleString()}</strong>
                  </span>
                  <span style={{ color: '#555' }}>
                    Payments: <strong>{paymentHistory.length}</strong>
                  </span>
                </div>
                {paymentInvoice.selected_currency && paymentInvoice.selected_currency !== 'PKR' && paymentInvoice.converted_amount && paymentInvoice.show_converted_currency !== 0 && (
                  <div style={{ color: '#1565C0', fontSize: '12px', marginTop: '2px' }}>
                    ≈ {CURRENCY_SYMBOLS[paymentInvoice.selected_currency]}{parseFloat(paymentInvoice.converted_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} {paymentInvoice.selected_currency}
                  </div>
                )}
              </div>

              {paymentInstallments.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', color: '#E65100', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
                    Installment Schedule
                  </div>
                  <div style={{ border: '1px solid #FFD8B0', borderRadius: '8px', overflow: 'hidden' }}>
                    {paymentInstallments.map((row, idx) => (
                      <div key={row.id || idx} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 12px', borderBottom: idx < paymentInstallments.length - 1 ? '1px solid #ffe8d1' : 'none',
                        background: idx % 2 ? '#FFF8F0' : '#fff', gap: '10px', fontSize: '13px'
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>#{row.installment_number || idx + 1}</div>
                          <div style={{ fontSize: '12px', color: '#777' }}>Due {formatDate(row.due_date)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700 }}>PKR {parseFloat(row.amount || 0).toLocaleString()}</div>
                          <div style={{
                            fontSize: '11px', fontWeight: 600,
                            color: row.status === 'Paid' ? '#2E7D32' : row.status === 'Partial' ? '#E65100' : '#666'
                          }}>
                            {row.status || 'Pending'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {paymentHistory.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
                    Payment History
                  </div>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                    {paymentHistory.map((p, idx) => {
                      const via = p.payment_method === 'cash'
                        ? 'Cash'
                        : (p.account_name || p.bank_name || (p.payment_method === 'bank' ? 'Bank' : null));
                      return (
                      <div key={p.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 12px', borderBottom: idx < paymentHistory.length - 1 ? '1px solid #f0f0f0' : 'none',
                        background: idx % 2 ? '#fafafa' : '#fff', gap: '10px'
                      }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '13px' }}>
                            {String(p.notes || '').toLowerCase() === 'advance' ? 'Advance' : `Payment #${idx + 1}`}
                          </div>
                          <div style={{ fontSize: '12px', color: '#777' }}>
                            {formatDate(p.payment_date)}
                            {via ? ` · ${via}` : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 700, color: '#2E7D32', fontSize: '13px' }}>
                            PKR {parseFloat(p.amount || 0).toLocaleString()}
                          </span>
                          <button
                            type="button"
                            title="View Receipt"
                            onClick={() => navigate(`/payment-receipt/${paymentInvoice.id}/${p.id}`)}
                            style={{ background: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer' }}
                          >
                            <FontAwesomeIcon icon={faReceipt} />
                          </button>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Payment Status</label>
                <select className="input-field" value={paymentForm.paymentStatus} onChange={e => setPaymentForm({ ...paymentForm, paymentStatus: e.target.value })}>
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                  <option value="Partially Paid">Partially Paid</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                  Status auto-updates to Paid / Partially Paid based on payment totals (Cancelled can be set manually).
                </div>
              </div>
              <div className="form-group">
                <label>Payment Amount (PKR)</label>
                <input
                  type="number"
                  className="input-field"
                  placeholder="Enter amount for this payment"
                  min="0"
                  step="0.01"
                  value={paymentForm.installmentAmount}
                  onChange={e => setPaymentForm({ ...paymentForm, installmentAmount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Payment Date</label>
                <input type="date" className="input-field" value={paymentForm.paymentDate} onChange={e => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })} />
              </div>
              {parseFloat(paymentForm.installmentAmount) > 0 && (
                <div className="form-group">
                  <label>Payment Method <span style={{ color: 'red' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: paymentForm.paymentMethod === 'bank' ? '12px' : '0' }}>
                    <button type="button" onClick={() => setPaymentForm({ ...paymentForm, paymentMethod: 'bank' })}
                      style={{ flex: 1, padding: '12px 14px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${paymentForm.paymentMethod === 'bank' ? '#F47B20' : '#e5e7eb'}`, background: paymentForm.paymentMethod === 'bank' ? '#FFF3E0' : '#fafafa', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s' }}>
                      <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: '16px', color: paymentForm.paymentMethod === 'bank' ? '#F47B20' : '#bbb' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: paymentForm.paymentMethod === 'bank' ? '#E65100' : '#555' }}>Bank Transfer</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>Select a bank account</div>
                      </div>
                      {paymentForm.paymentMethod === 'bank' && <FontAwesomeIcon icon={faCheck} style={{ marginLeft: 'auto', color: '#F47B20', fontSize: '14px' }} />}
                    </button>
                    <button type="button" onClick={() => setPaymentForm({ ...paymentForm, paymentMethod: 'cash', bankAccountId: '' })}
                      style={{ flex: 1, padding: '12px 14px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${paymentForm.paymentMethod === 'cash' ? '#4CAF50' : '#e5e7eb'}`, background: paymentForm.paymentMethod === 'cash' ? '#E8F5E9' : '#fafafa', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s' }}>
                      <FontAwesomeIcon icon={faMoneyBill} style={{ fontSize: '16px', color: paymentForm.paymentMethod === 'cash' ? '#4CAF50' : '#bbb' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: paymentForm.paymentMethod === 'cash' ? '#2E7D32' : '#555' }}>Cash</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>No bank account needed</div>
                      </div>
                      {paymentForm.paymentMethod === 'cash' && <FontAwesomeIcon icon={faCheck} style={{ marginLeft: 'auto', color: '#4CAF50', fontSize: '14px' }} />}
                    </button>
                  </div>
                  {paymentForm.paymentMethod === 'bank' && (
                    <select
                      className="input-field"
                      value={paymentForm.bankAccountId}
                      onChange={e => setPaymentForm({ ...paymentForm, bankAccountId: e.target.value })}
                      style={{ marginBottom: 0 }}
                    >
                      <option value="">Select Bank Account</option>
                      {bankAccounts.map(ba => (
                        <option key={ba.id} value={ba.id}>{ba.account_name} — {ba.bank_name} ({ba.currency})</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {canViewReceipt(paymentInvoice) && (
                <button
                  className="btn-secondary"
                  style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }}
                  onClick={() => openReceiptForInvoice({ ...paymentInvoice, payments: paymentHistory })}
                >
                  <FontAwesomeIcon icon={faReceipt} style={{ marginRight: '6px' }} />
                  View Receipt{paymentHistory.length > 1 ? 's' : ''}
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowPaymentModal(false)}>Close</button>
              <button className="btn-primary" onClick={handleUpdatePayment} disabled={updatingPayment}>
                {updatingPayment
                  ? 'Saving...'
                  : (parseFloat(paymentForm.installmentAmount) > 0 ? 'Record Payment' : 'Update Status')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Receipt picker for multiple installments */}
      {receiptPicker && (
        <div className="modal-overlay" onClick={() => setReceiptPicker(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faReceipt} style={{ marginRight: '10px', color: '#2e7d32' }} />Select Receipt</h2>
              <button className="modal-close" onClick={() => setReceiptPicker(null)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 12px', color: '#666', fontSize: '13px' }}>
                This invoice has {receiptPicker.payments.length} payments. Choose which receipt to view.
              </p>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                {receiptPicker.payments.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      const invId = receiptPicker.invoice.id;
                      setReceiptPicker(null);
                      navigate(`/payment-receipt/${invId}/${p.id}`);
                    }}
                    style={{
                      width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 14px', border: 'none', borderBottom: idx < receiptPicker.payments.length - 1 ? '1px solid #f0f0f0' : 'none',
                      background: '#fff', cursor: 'pointer', textAlign: 'left'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px' }}>
                        {String(p.notes || '').toLowerCase() === 'advance' ? 'Advance' : `Payment #${idx + 1}`}
                      </div>
                      <div style={{ fontSize: '12px', color: '#777' }}>{formatDate(p.payment_date)}</div>
                    </div>
                    <div style={{ fontWeight: 700, color: '#2E7D32' }}>
                      PKR {parseFloat(p.amount || 0).toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setReceiptPicker(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faTrash} style={{ marginRight: '10px', color: '#F44336' }} />Delete Invoice</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete invoice <strong>{invoiceToDelete?.invoice_id}</strong>? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="btn-primary" style={{ background: '#F44336' }} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}